import type { JSX } from "react";

import { Button, Flex, Space, Stack } from "@mantine/core";
import { isEmail, isNotEmpty, useForm } from "@mantine/form";
import { useState } from "react";

import type { Intent } from "../../../../../../types";
import type { StepProps } from "../models";

import { useSalesContext } from "../../../../../../sales-context/SalesContext";
import { isInternationalCaller } from "../../../../../../shared-utils";
import {
    useAnalytics,
    withPolymorphicClickAnalytics,
} from "../../../../../../shared-utils/analytics/withPolymorphicClickAnalytics";
import { HandshakeAnimation } from "../../../../../../shared-utils/common-ui/animations/handshake";
import { useVerifiedCustomerStore } from "../../../../../../stores";
import { assistMessageSuggestions } from "../../../../../../types";
import { IVRSummary } from "../../../customer-handoff/IVRSummary";
import { Header } from "../Header";
import { NavButtons } from "../NavButtons";
import { SalesQuestionnaire } from "../sales-questionnaire/SalesQuestionnaire";
import { CallReasonForm } from "./CallReasonForm";
import { CustomerInfoForm } from "./CustomerInfoForm";

function isEnoughInfoGathered(
    isSalesQuestionnaireEnabled: boolean,
    { currentPlan }: { currentPlan: string | null },
): boolean {
    return !isSalesQuestionnaireEnabled || currentPlan === "No plan" || currentPlan !== null;
}

export function CustomerInfoCollectionStep({ onNext, onPrev }: StepProps): JSX.Element {
    const { sessionId, ivrInfo, taskData, partnerConfig, timeline } = useSalesContext();
    const {
        features: { isSalesQuestionnaireEnabled = false },
        salesQuestionnaireProps: { plans } = {},
    } = partnerConfig;
    const { dispatcher } = useAnalytics();
    const customer = useVerifiedCustomerStore((state) => state.getVerifiedCustomer(sessionId).customer);
    const setCustomer = useVerifiedCustomerStore((state) => state.setCustomer);
    const hasInfoFromIVR = !!ivrInfo?.intent || !!ivrInfo?.summary;

    const [intent, setIntent] = useState<Intent | undefined>(
        (customer?.intent as Intent | undefined) ?? (ivrInfo?.intent as Intent | undefined),
    );
    const ButtonWithAnalytics = withPolymorphicClickAnalytics(Button<"button">, "Click");

    const form = useForm({
        validateInputOnBlur: true,
        initialValues: {
            email: customer?.email ?? ivrInfo?.email ?? "",
            firstName: customer?.firstName ?? "",
            lastName: customer?.lastName ?? "",
            isInternationalCaller: taskData.mdn ? isInternationalCaller(taskData.mdn) : false,
            currentPlan: customer?.productName ?? null,
        },
        validate: {
            // fixme: remove type assertions once a fix lands for [form.isValid(): Type 'Promise<boolean>' is not assignable to type 'boolean'. · Issue #8880 · mantinedev/mantine](https://github.com/mantinedev/mantine/issues/8880)
            email: isEmail("Invalid email") as (value: unknown) => Awaited<React.ReactNode>,
            firstName: isNotEmpty("Enter the customer's first name") as (value: unknown) => Awaited<React.ReactNode>,
            lastName: isNotEmpty("Enter the customer's last name") as (value: unknown) => Awaited<React.ReactNode>,
        },
    });

    const handleNext =
        intent && onNext
            ? () => {
                  setCustomer(sessionId, {
                      ...customer,
                      ...form.getValues(),
                      fullName: `${form.getValues().firstName} ${form.getValues().lastName}`,
                      intent,
                      mdn: taskData.mdn,
                      productName: form.getValues().currentPlan ?? undefined,
                      metadata: {
                          isInternationalCaller: form.getValues().isInternationalCaller,
                      },
                  });
                  if (timeline) {
                      timeline.addMessage(sessionId, {
                          id: crypto.randomUUID(),
                          text: assistMessageSuggestions[intent],
                          type: "Default",
                          isUnread: true,
                          status: "active",
                          key: "intentDetected",
                      });
                  }
                  onNext();
                  void dispatcher.dispatchBusinessEvent("SalesQuestionnaireInfoGathered", {
                      isInternationalCaller: form.getValues().isInternationalCaller,
                      currentPlan: form.getValues().currentPlan,
                  });
                  void dispatcher.dispatchBusinessEvent("CustomerIntentCollected", {
                      intent,
                  });
              }
            : undefined;

    return (
        <Stack mt="sm" w="100%">
            {hasInfoFromIVR && (
                <Stack align="center">
                    <HandshakeAnimation width="40%" />
                    <Header text="IVR handoff" />
                </Stack>
            )}
            {ivrInfo?.intent && intent ? (
                <Stack gap={0}>
                    <IVRSummary intent={intent} summary={ivrInfo.summary} />
                    <Flex w="100%" justify="flex-end">
                        <ButtonWithAnalytics
                            variant="subtle"
                            c="dark.0"
                            td="underline"
                            p="0"
                            onClick={() => setIntent(undefined)}
                            analytics={{ actionId: "IncorrectCallReason" }}
                        >
                            Change reason
                        </ButtonWithAnalytics>
                    </Flex>
                </Stack>
            ) : (
                <CallReasonForm intent={intent} setIntent={setIntent} />
            )}
            <Stack gap={0} w="100%">
                <CustomerInfoForm form={form} />
                <Space h="md" />
                {plans && (
                    <>
                        <SalesQuestionnaire form={form} plans={plans} />
                        <Space h="md" />
                    </>
                )}
                <NavButtons
                    onNext={
                        form.isValid() &&
                        isEnoughInfoGathered(isSalesQuestionnaireEnabled, form.values) &&
                        intent &&
                        onNext
                            ? handleNext
                            : undefined
                    }
                    onPrev={onPrev}
                />
            </Stack>
        </Stack>
    );
}
